/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addInvalidatePredictions } from 'src/core/utils/decorators/addInvalidatePredictions';
import { getRegexEntityService } from 'src/luis/core/services/entities/regexEntityService';
import { getEntityHandlerGroupUtils } from 'src/luis/projects/entities/utils/entityHelpers';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';

export type RegexEntityHandlerGroup = ReturnType<typeof createRegexEntityHandlerGroup>;

/**
 * Represents a handler group for regex entity operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createRegexEntityHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const { addOrReplaceEntityStore, getAppVersionStore, getEntityStore } = getEntityHandlerGroupUtils(rootStore);
    const logger = getLogger();

    /**
     * Loads a given regex entity.
     *
     * @param id The id of the regex entity to load.
     */
    const loadRegexEntity = async ({ id, context }: HandlerParams<Pick<RegexEntityStore, 'id'>>) => {
        const { appId, versionId } = context;
        const regexEntityService = getRegexEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger<RegexEntityStore>('LoadRegexEntity', () => ({ entityId: id }));

        const regexEntityStore = await logTimedActivity(regexEntityService.getEntity({ id }));

        addOrReplaceEntityStore({ appId, versionId }, regexEntityStore);
    };

    /**
     * Creates a new regex entity.
     *
     * @param name The regex entity name.
     * @param pattern The regex entity pattern.
     */
    const createRegexEntity = async ({ name, pattern, context, tracker }: HandlerParams<Pick<RegexEntityStore, 'name' | 'pattern'>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const regexEntityService = getRegexEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger<RegexEntityStore>('CreateRegexEntity', (result, error) => {
                const logData = { entityName: name, pattern };

                return error ? logData : { ...logData, entityId: result.id };
            });

            const regexEntityStore = await logTimedActivity(regexEntityService.createEntity({ name, pattern }));

            getAppVersionStore({ appId, versionId }).entityStoreHouse.add(regexEntityStore);

            if (tracker) {
                tracker.setContext(regexEntityStore);
            }
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Creates a new regex entity.
     *
     * @param id The id of the entity to update.
     * @param name Optional: the regex entity new name.
     * @param pattern Optional: the regex entity new pattern.
     */
    const updateRegexEntity = async ({
        id,
        name,
        pattern,
        context
    }: HandlerParams<Partial<Pick<RegexEntityStore, 'name' | 'pattern'>> & Pick<RegexEntityStore, 'id'>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const regexEntityService = getRegexEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger('UpdateRegexEntity', () => ({ entityId: id }));

            await logTimedActivity(regexEntityService.updateEntity({ id, name, pattern }));

            const entityStore: RegexEntityStore = getEntityStore({ appId, versionId, entityId: id });
            entityStore.rename(name);
            entityStore.updatePattern(pattern);
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Delete the given regex entity.
     *
     * @param id The id of regex entity to delete.
     */
    const deleteRegexEntity = async ({ id, context, tracker }: HandlerParams<Pick<RegexEntityStore, 'id'>>) => {
        if (tracker) {
            tracker.setContext(id);
        }

        const { appId, versionId } = context;
        const regexEntityService = getRegexEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger('DeleteRegexEntity', () => ({ entityId: id }));

        await logTimedActivity(regexEntityService.deleteEntity({ id }));

        const appVersionStore = getAppVersionStore({ appId, versionId });
        appVersionStore.entityStoreHouse.removeById(id);
    };

    return {
        loadRegexEntity,
        createRegexEntity,
        updateRegexEntity,
        deleteRegexEntity
    };
};
